/**
 * Parses the `ref` payload that Facebook Messenger forwards when a
 * customer opens a conversation via `m.me/<page>?ref=<payload>`.
 *
 * Ref shape (constructed by the PDP "Message us" button —
 * see _storefront.products.$handle.tsx):
 *
 *   p_<product-handle>                                                                   (handle only)
 *   p_<product-handle>_v_<variant-sku>                                                   (+ variant)
 *   p_<product-handle>_v_<variant-sku>_l_<locale>                                        (+ locale, "vi" | "en")
 *   p_<product-handle>_v_<variant-sku>_l_<locale>_pr_<priceMinor>_cc_<currency>          (+ market price)
 *   p_<product-handle>_v_<variant-sku>_l_<locale>_pr_<priceMinor>_cc_<currency>__src_<x> (+ ad attribution)
 *
 * Segment order is fixed so a missing middle field can be parsed
 * cleanly. The `__src_` segment lives after a DOUBLE underscore so it
 * never collides with the primary segments which use single underscores.
 *
 * Encoding rules:
 *   - The PDP URL-encodes the whole ref; Meta decodes it once before
 *     forwarding to our webhook, so this parser receives the raw
 *     post-decode string (no decodeURIComponent needed here).
 *   - Handles are slugified (a-z, 0-9, hyphen). SKUs are alphanumeric
 *     + hyphens. Locale is strictly "vi" or "en". Source tokens can
 *     include letters, digits, hyphens, underscores.
 *   - Price is encoded as an integer in MINOR UNITS — i.e.
 *     `Math.round(displayPrice * 100)`. The decoder divides back by
 *     100 to recover the display value. This keeps the wire format
 *     digits-only (Math.round drops decimal noise) and works for both
 *     subunit-bearing currencies (USD/SGD = cents) and VND (which has
 *     no subunit — for VND a 500.000đ price encodes as `50000000`).
 *     Currency code is ISO 4217, exactly 3 uppercase letters.
 *
 * Defensive — returns null for any unrecognised shape (the webhook
 * then falls through to a plain NEW_MESSAGE notification with no
 * product link, so a malformed ref never blocks the conversation).
 */

export interface ParsedRef {
  productHandle?: string;
  variantSku?: string;
  source?: string;
  /** Locale of the PDP the customer was on when they clicked the
   *  Messenger button — drives which language the v2 auto-reply uses
   *  ("vi" → Vietnamese template, "en" → English template).
   *  Optional + the caller can default to "vi" if absent. */
  locale?: "vi" | "en";
  /** Price the customer was looking at on the PDP, in MINOR units —
   *  i.e. `Math.round(displayPrice * 100)`. Divide by 100 to format.
   *  Lets the auto-reply echo back the EXACT market-resolved price
   *  the customer just saw (S$28 for a Singapore visitor, 500.000đ
   *  for a Vietnam visitor) instead of always falling back to the
   *  DB-stored VND retail price. Always paired with `currency`. */
  priceMinor?: number;
  /** ISO 4217 currency code (e.g. "VND", "SGD", "USD", "AUD") that
   *  pairs with `priceMinor` to drive the auto-reply's Intl.NumberFormat
   *  currency display. Always paired with `priceMinor`. */
  currency?: string;
  /** Short-lived HMAC-signed token bridging a logged-in retail
   *  customer session to this Messenger conversation. Generated
   *  server-side by the PDP loader when the visitor is authenticated;
   *  validated by the webhook via verifyCustomerToken (which checks
   *  HMAC + expiry). Format: `<cuid>~<expiresAtMs>~<hmac16>` — tilde
   *  separator avoids collision with the refParser's `_` / `__` /
   *  `.` separators. Caller passes it to verifyCustomerToken to get
   *  back the customerId. */
  customerToken?: string;
}

const MAX_REF_LENGTH = 1000; // Meta caps ref at 2000 chars; we cap shorter to refuse pathological input

export function parseRef(ref: string | null | undefined): ParsedRef | null {
  if (!ref || typeof ref !== "string") return null;
  const trimmed = ref.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_REF_LENGTH) return null;

  // Split off the optional source segment first.
  const [primary, ...rest] = trimmed.split("__");
  let source: string | undefined;
  if (rest.length > 0) {
    // Re-join in case the source value itself contained `__` (unlikely but defensive).
    const sourcePart = rest.join("__");
    // Expect `src_<value>` prefix; strip it.
    if (sourcePart.startsWith("src_")) {
      source = sourcePart.slice(4) || undefined;
    } else {
      // Unknown segment shape — keep going with the primary, ignore the tail.
      source = undefined;
    }
  }

  // Primary must start with `p_` for the product identifier.
  if (!primary.startsWith("p_")) return null;
  const afterP = primary.slice(2);
  if (!afterP) return null;

  // The primary segment can carry up to 6 sub-fields, in order:
  //   p_<handle>             (required)
  //   _v_<sku>               (optional — empty if customer hadn't picked a size)
  //   _l_<locale>            (optional — vi | en)
  //   _pr_<priceMinor>       (optional — integer, Math.round(displayPrice * 100))
  //   _cc_<currency>         (optional — ISO 4217, 3 uppercase letters)
  //   _ct_<customerToken>    (optional — HMAC token from signCustomerToken,
  //                          tilde-separated, 50-60 chars)
  // Peel trailing segments right-to-left so missing middle fields don't
  // break the parse. Order: customerToken → currency → price → locale
  // → variant → handle.
  let working = afterP;

  // Customer token (HMAC-signed bridge token from logged-in PDP visit).
  // Validated only structurally here; the webhook calls
  // verifyCustomerToken to check the HMAC.
  let customerToken: string | undefined;
  const ctIdx = working.lastIndexOf("_ct_");
  if (ctIdx !== -1) {
    const ctVal = working.slice(ctIdx + 4);
    // Token shape: <cuid>~<digits>~<base64url-16>
    //   cuid: [a-z0-9]+
    //   digits: [0-9]+
    //   base64url: [A-Za-z0-9_-]+ (16 chars exactly)
    if (/^[a-z0-9]+~\d+~[A-Za-z0-9_-]{16}$/.test(ctVal)) {
      customerToken = ctVal;
      working = working.slice(0, ctIdx);
    }
  }

  // Currency (must be exactly 3 uppercase letters — ISO 4217).
  let currency: string | undefined;
  const ccIdx = working.lastIndexOf("_cc_");
  if (ccIdx !== -1) {
    const ccVal = working.slice(ccIdx + 4);
    if (/^[A-Z]{3}$/.test(ccVal)) {
      currency = ccVal;
      working = working.slice(0, ccIdx);
    }
  }

  // Price in minor units (digits only — buildRef pre-rounds + multiplies).
  let priceMinor: number | undefined;
  const prIdx = working.lastIndexOf("_pr_");
  if (prIdx !== -1) {
    const prVal = working.slice(prIdx + 4);
    if (/^\d+$/.test(prVal)) {
      const n = parseInt(prVal, 10);
      // Sanity cap: 10 trillion minor units (covers VND prices up to
      // 100 billion VND, way past any apparel SKU). Anything larger is
      // either a parse error or hostile input — drop the price segment.
      if (Number.isFinite(n) && n >= 0 && n <= 1e13) {
        priceMinor = n;
        working = working.slice(0, prIdx);
      }
    }
  }

  let locale: "vi" | "en" | undefined;
  const localeIdx = working.lastIndexOf("_l_");
  if (localeIdx !== -1) {
    const localeVal = working.slice(localeIdx + 3);
    if (localeVal === "vi" || localeVal === "en") {
      locale = localeVal;
      working = working.slice(0, localeIdx);
    }
  }

  // Variant marker next.
  const variantMarker = "_v_";
  const idx = working.indexOf(variantMarker);
  let productHandle: string;
  let variantSku: string | undefined;
  if (idx === -1) {
    productHandle = working;
  } else {
    productHandle = working.slice(0, idx);
    variantSku = working.slice(idx + variantMarker.length) || undefined;
  }

  if (!productHandle) return null;

  // Defensive: handles are slugified (a-z, 0-9, hyphen); SKUs are
  // alphanumeric + hyphens. Reject anything with whitespace, control
  // chars, or shell-injection-shaped characters in case the ref
  // somehow survived URL encoding mangled.
  if (!/^[a-z0-9-]+$/i.test(productHandle)) return null;
  if (variantSku && !/^[a-z0-9-]+$/i.test(variantSku)) {
    // Don't reject the whole ref — just drop the variant; the product
    // lookup is still useful for CS context.
    variantSku = undefined;
  }
  if (source && !/^[a-zA-Z0-9_-]+$/i.test(source)) {
    source = undefined;
  }

  const out: ParsedRef = { productHandle };
  if (variantSku) out.variantSku = variantSku;
  if (source) out.source = source;
  if (locale) out.locale = locale;
  // Only surface price+currency as a pair — a half-encoded segment
  // would render as a bare number or a bare ISO code, neither of which
  // is useful in the auto-reply, so we'd rather fall back to the
  // DB-stored VND price in that case.
  if (priceMinor !== undefined && currency) {
    out.priceMinor = priceMinor;
    out.currency = currency;
  }
  if (customerToken) out.customerToken = customerToken;
  return out;
}

/**
 * Inverse of parseRef. Used by the PDP button to construct the
 * outgoing m.me link. Kept here so the encoding + decoding live next
 * to each other and can be unit-tested as a round trip.
 */
export function buildRef(input: {
  productHandle: string;
  variantSku?: string | null;
  source?: string | null;
  locale?: "vi" | "en" | null;
  /** Display price (i.e. what the customer sees on the PDP — e.g. `28`
   *  for S$28.00 or `500000` for 500.000đ). Multiplied by 100 + rounded
   *  on the wire so the ref stays digits-only. Only emitted when paired
   *  with a valid `currency`. */
  price?: number | null;
  /** ISO 4217 currency code (3 uppercase letters). Lowercase / invalid
   *  codes are silently dropped — the receiver falls back to the
   *  DB-stored VND price in that case. */
  currency?: string | null;
  /** Customer-token from signCustomerToken — only emitted when the
   *  PDP visitor is an authenticated retail customer. Lets the
   *  webhook deterministically link the inbound PSID to the customer
   *  row without phone/email guessing. */
  customerToken?: string | null;
}): string {
  let ref = `p_${input.productHandle}`;
  if (input.variantSku) ref += `_v_${input.variantSku}`;
  if (input.locale === "vi" || input.locale === "en") {
    ref += `_l_${input.locale}`;
  }
  // Price + currency are encoded together — emitting one without the
  // other would leave the receiver unable to format the number, so we
  // require both before emitting either.
  if (
    input.price !== null &&
    input.price !== undefined &&
    Number.isFinite(input.price) &&
    input.price > 0 &&
    input.currency &&
    /^[A-Z]{3}$/.test(input.currency)
  ) {
    const priceMinor = Math.round(input.price * 100);
    if (priceMinor > 0 && priceMinor <= 1e13) {
      ref += `_pr_${priceMinor}_cc_${input.currency}`;
    }
  }
  // Customer token — emitted last among the primary-segment fields,
  // before the `__src_` segment. Strict shape check so a malformed
  // token doesn't pollute the ref (the receiver re-validates the
  // HMAC; this is just a wire-format sanity gate).
  if (
    input.customerToken &&
    /^[a-z0-9]+~\d+~[A-Za-z0-9_-]{16}$/.test(input.customerToken)
  ) {
    ref += `_ct_${input.customerToken}`;
  }
  if (input.source) ref += `__src_${input.source}`;
  return ref;
}
